import { api } from '../middleware/api';
import { sseClient } from '../../services/sseClient';

// Define endpoints using injectApi pattern
export const foecheckinApi = api
  .enhanceEndpoints({ addTagTypes: ['FOECheckin'] })
  .injectEndpoints({
    endpoints: builder => ({
      getFOECheckins: builder.query<any[], string>({
        query: cid => `foecheckin/${cid}`,
        providesTags: ['FOECheckin'],
        async onCacheEntryAdded(
          _arg,
          { cacheDataLoaded, cacheEntryRemoved, dispatch }
        ) {
          try {
            await cacheDataLoaded;
            const unsubscribe = sseClient.addListener(data => {
              if (
                data.type === 'FRONT_OFFICE_UPDATED' ||
                data.type === 'POS_ORDER_UPDATED'
              ) {
                dispatch(foecheckinApi.util.invalidateTags(['FOECheckin']));
              }
            });
            await cacheEntryRemoved;
            unsubscribe();
          } catch {
            // no-op
          }
        }
      }),
      getFOEGRCs: builder.query<any[], string>({
        query: cid => `foecheckin/grc/${cid}`,
        providesTags: ['FOECheckin']
      }),
      getFOEMobile: builder.query<any[], any>({
        query: ({ cid, mobile }) => `foecheckin/bymobile/${cid}/${mobile}`,
        providesTags: ['FOECheckin']
      }),
      getFOEOccRooms: builder.query<any, any>({
        query: ({ cid, coid }) => `foecheckin/occrooms/${cid}/${coid}`,
        providesTags: ['FOECheckin']
      }),
      getFOEOccs: builder.query<any[], string>({
        query: cid => `foecheckin/occs/${cid}`,
        providesTags: ['FOECheckin'],
        async onCacheEntryAdded(
          _arg,
          { cacheDataLoaded, cacheEntryRemoved, dispatch }
        ) {
          try {
            await cacheDataLoaded;
            const unsubscribe = sseClient.addListener(data => {
              if (
                data.type === 'FRONT_OFFICE_UPDATED' ||
                data.type === 'POS_ORDER_UPDATED'
              ) {
                dispatch(foecheckinApi.util.invalidateTags(['FOECheckin']));
              }
            });
            await cacheEntryRemoved;
            unsubscribe();
          } catch {
            // no-op
          }
        }
      }),
      getFOECheckinById: builder.query<any, string>({
        query: id => `foecheckin/byid/${id}`,
        providesTags: ['FOECheckin']
      }),
      getFOECheckinByDate: builder.query<any, any>({
        query: ({ cid, fdt, tdt }) => `foecheckin/bydate/${cid}/${fdt}/${tdt}`,
        providesTags: ['FOECheckin']
      }),
      getFOECheckinOccByDate: builder.query<any, any>({
        query: ({ cid, fdt, tdt }) =>
          `foecheckin/byoccdate/${cid}/${fdt}/${tdt}`,
        providesTags: ['FOECheckin']
      }),
      insertImage: builder.mutation<{}, FormData>({
        query: (data: FormData) => ({
          url: `foecheckin/insertimage`,
          method: 'POST',
          body: data
        })
      }),
      insertFOECGRC: builder.mutation({
        query: company => ({
          url: `foecheckin/grc`,
          method: 'POST',
          body: company
        }),
        invalidatesTags: ['FOECheckin']
      }),
      insertFOECheckin: builder.mutation({
        query: foecheckin => ({
          url: `foecheckin/`,
          method: 'POST',
          body: foecheckin
        }),
        invalidatesTags: ['FOECheckin']
      }),
      updateFOECheckin: builder.mutation({
        query: foecheckin => ({
          url: `foecheckin/`,
          method: 'PUT',
          body: foecheckin
        }),
        invalidatesTags: ['FOECheckin']
      }),
      updateDeparture: builder.mutation({
        query: foecheckin => ({
          url: `foecheckin/extend/`,
          method: 'PUT',
          body: foecheckin
        }),
        invalidatesTags: ['FOECheckin']
      }),
      deleteFOECheckin: builder.mutation({
        query: foecheckin => ({
          url: `foecheckin/${foecheckin.ci_id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['FOECheckin']
      })
    }),
    overrideExisting: true
  });

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetFOECheckinsQuery,
  useGetFOEGRCsQuery,
  useGetFOEMobileQuery,
  useGetFOEOccsQuery,
  useGetFOEOccRoomsQuery,
  useGetFOECheckinByIdQuery,
  useGetFOECheckinByDateQuery,
  useGetFOECheckinOccByDateQuery,
  useInsertImageMutation,
  useInsertFOECGRCMutation,
  useInsertFOECheckinMutation,
  useUpdateFOECheckinMutation,
  useUpdateDepartureMutation,
  useDeleteFOECheckinMutation
} = foecheckinApi;

